import { useEffect, useMemo, useState } from "react";
import { Alert, Slider, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { PageSectionCard } from "../../../components/layout";
import { AppFormField, AppFormGrid } from "../../../components/Page";
import { AppAutocomplete } from "../../../components/form";
import { GRID_FULL, GRID_HALF } from "../../../lib/ui/formLayout";
import { adminDataAdaptorService, isWarehouseAdaptorType, type DataAdaptor } from "../../../services/adminDataAdaptorService";
import { semanticModelService, type SemanticModel } from "../../../services/semanticModelService";
import type { AnomalyDefinition } from "../../../types/anomaly";

/** Warehouse buckets are calendar grains; these map to hour / day / week / month on the backend. */
export const WAREHOUSE_BUCKETS = ["1h", "1d", "1w", "30d"];

type Props = {
  def: AnomalyDefinition;
  onChange: (patch: Partial<AnomalyDefinition>) => void;
  lookbackOptions: string[];
  cronOptions: string[];
};

/**
 * Series source for WAREHOUSE anomaly definitions: semantic metrics (and an optional dimension) of an adaptor's
 * approved semantic model. The engine aggregates per bucket; the backend scores the latest bucket (MAD z-score).
 */
export default function WarehouseAnomalySourceCard({ def, onChange, lookbackOptions, cronOptions }: Props) {
  const { t } = useTranslation();
  const [adaptors, setAdaptors] = useState<DataAdaptor[]>([]);
  const [model, setModel] = useState<SemanticModel | null>(null);

  useEffect(() => {
    adminDataAdaptorService
      .list()
      .then((list) => setAdaptors((list || []).filter((a) => isWarehouseAdaptorType(a.type) && a.enabled)))
      .catch(() => setAdaptors([]));
  }, []);

  useEffect(() => {
    if (!def.adaptorId) {
      setModel(null);
      return;
    }
    semanticModelService
      .byAdaptor(def.adaptorId)
      .then(setModel)
      .catch(() => setModel(null));
  }, [def.adaptorId]);

  const metrics = useMemo(() => (model?.metrics || []).map((m) => m.name), [model]);
  const dimensions = useMemo(() => (model?.dimensions || []).map((d) => d.name), [model]);
  const selectedAdaptor = useMemo(
    () => adaptors.find((a) => a.id === def.adaptorId) ?? null,
    [adaptors, def.adaptorId]
  );
  const dimensionOptions = useMemo(() => ["", ...dimensions], [dimensions]);
  const bucket =
    WAREHOUSE_BUCKETS.includes(def.bucketInterval || "") ? (def.bucketInterval as string) : "1d";

  return (
    <PageSectionCard title={t("anomaly.warehouse.title")} description={t("anomaly.warehouse.help")}>
      <AppFormGrid>
        <AppFormField size={GRID_HALF}>
          <AppAutocomplete<DataAdaptor>
            label={t("anomaly.warehouse.adaptor")}
            options={adaptors}
            value={selectedAdaptor}
            getOptionLabel={(a) => `${a.name} · ${a.type}`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, value) =>
              onChange({
                adaptorId: value?.id || "",
                semanticMetrics: [],
                semanticDimension: undefined,
              })
            }
          />
        </AppFormField>
        <AppFormField size={GRID_HALF}>
          {def.adaptorId && model?.status !== "APPROVED" ? (
            <Alert severity="warning">{t("anomaly.warehouse.noApprovedModel")}</Alert>
          ) : null}
        </AppFormField>
        <AppFormField size={GRID_HALF}>
          <AppAutocomplete<string, true>
            multiple
            label={t("anomaly.warehouse.metrics")}
            options={metrics}
            value={def.semanticMetrics || []}
            onChange={(_, value) => onChange({ semanticMetrics: value })}
          />
        </AppFormField>
        <AppFormField size={GRID_HALF}>
          <AppAutocomplete<string>
            label={t("anomaly.warehouse.dimension")}
            options={dimensionOptions}
            value={def.semanticDimension || ""}
            getOptionLabel={(d) => (d ? d : t("anomaly.warehouse.noDimension"))}
            onChange={(_, value) => onChange({ semanticDimension: value || undefined })}
          />
        </AppFormField>
        <AppFormField size={GRID_HALF}>
          <AppAutocomplete<string>
            label={t("anomaly.bucketInterval")}
            options={WAREHOUSE_BUCKETS}
            value={bucket}
            disableClearable
            onChange={(_, value) => value && onChange({ bucketInterval: value })}
          />
        </AppFormField>
        <AppFormField size={GRID_HALF}>
          <AppAutocomplete<string>
            label={t("anomaly.lookback")}
            helperText={t("anomaly.lookbackHelp")}
            options={lookbackOptions}
            value={def.lookback || null}
            onChange={(_, value) => onChange({ lookback: value || "" })}
          />
        </AppFormField>
        <AppFormField size={GRID_FULL}>
          <AppAutocomplete<string>
            label={t("anomaly.scheduleCron")}
            helperText={t("anomaly.scheduleCronHelp")}
            options={cronOptions}
            value={def.scheduleCron || null}
            onChange={(_, value) => onChange({ scheduleCron: value || "" })}
          />
        </AppFormField>
        <AppFormField size={GRID_FULL}>
          <Typography variant="body2">
            {t("anomaly.sensitivity")}: {(def.sensitivity ?? 0.5).toFixed(2)}
          </Typography>
          <Slider
            min={0}
            max={1}
            step={0.05}
            value={def.sensitivity ?? 0.5}
            onChange={(_, v) => onChange({ sensitivity: v as number })}
            valueLabelDisplay="auto"
          />
        </AppFormField>
      </AppFormGrid>
    </PageSectionCard>
  );
}
